import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useUI } from '@/hooks/ui/useUI';
import { useAbortController } from '@/hooks/ui/useAbortController';
import { useDebounce } from '@/hooks/ui/useDebounce';
import { useCatalogLoader } from '@/hooks/ui/useCatalogLoader';
import { useSecurityGroupsByPermission } from '@/hooks/securityGrouping/useSecurityGroupsByPermission';
import { holdingService } from '@/api/domains/catalog/holding/holding.service';
import { areasCatalogService } from '@/api/domains/catalog/areas/areas.service.ts';
import { positionsCatalogService } from '@/api/domains/catalog/positions/positions.service.ts';
import { costCentersCatalogService } from '@/api/domains/catalog/costCenters/costCenters.service.ts';
import { locationsCatalogService } from '@/api/domains/catalog/locations/locations.service';
import { contractCatalogService } from '@/api/domains/catalog/contract/contract.service.ts';
import { employeeFilteredService } from
  '@/api/domains/employee/services/employeeFiltered.service';
import type {
  GetEmployeesFilteredParams,
  FilteredEmployee,
  EmployeeFilteredStatus,
} from '@/api/domains/employee/types';
import { EMPLOYEE_FILTER_PAGE_SIZE } from '@/api/domains/employee/types';
import { toStringArray } from '@/utils';


const DEBOUNCE_DELAY = 500;

// Configuration object — each boolean controls whether the filter is rendered
// AND whether its API call is made
export interface DimensionalFilterConfig {
  company?: boolean;
  area?: boolean;
  position?: boolean;
  costCenter?: boolean;
  location?: boolean;
  contractModality?: boolean;
  securityGroup?: boolean;
  employeeStatus?: boolean;
  employee?: boolean;
}

// Internal state for all dimensional filter values
export interface DimensionalFilterState {
  companyIds: string[];
  areaIds: string[];
  positionIds: string[];
  costCenterIds: string[];
  locationIds: string[];
  contractModalityIds: string[];
  groupIds: string[];
  status: EmployeeFilteredStatus;
}

const DEFAULT_FILTER_STATE: DimensionalFilterState = {
  companyIds: [],
  areaIds: [],
  positionIds: [],
  costCenterIds: [],
  locationIds: [],
  contractModalityIds: [],
  groupIds: [],
  status: 'vigente',
};

function buildInitialFilterState(initialStatus?: EmployeeFilteredStatus): DimensionalFilterState {
  return initialStatus ? { ...DEFAULT_FILTER_STATE, status: initialStatus } : DEFAULT_FILTER_STATE;
}

export interface UseEmployeeDimensionalFilterResult {
  dimensionalFilters: DimensionalFilterState;
  selectedEmployeeIds: number[];
  availableEmployees: FilteredEmployee[];
  isLoadingEmployees: boolean;
  isLoadingMoreEmployees: boolean;
  employeeHasMore: boolean;
  loadMoreEmployees: () => Promise<void>;
  isLoadingPositions: boolean;
  isExpanded: boolean;
  toggleExpanded: () => void;
  catalogOptions: {
    companyOptions: { value: string; label: string }[];
    areaOptions: { value: string; label: string }[];
    positionOptions: { value: string; label: string }[];
    costCenterOptions: { value: string; label: string }[];
    locationOptions: { value: string; label: string }[];
    contractModalityOptions: { value: string; label: string }[];
    securityGroupOptions: { value: string; label: string }[];
    employeeOptions: { value: string; label: string }[];
  };
  handlers: {
    onCompanyChange: (ids: string | string[]) => void;
    onAreaChange: (ids: string | string[]) => void;
    onPositionChange: (ids: string | string[]) => void;
    onCostCenterChange: (ids: string | string[]) => void;
    onLocationChange: (ids: string | string[]) => void;
    onContractModalityChange: (ids: string | string[]) => void;
    onSecurityGroupChange: (ids: string | string[]) => void;
    onEmployeeStatusChange: (status: string | string[]) => void;
    onEmployeeSelectionChange: (ids: string | string[]) => void;
    onEmployeeSearchChange: (search: string) => void;
  };
}

export function useEmployeeDimensionalFilter(
  config: DimensionalFilterConfig,
  initialStatus?: EmployeeFilteredStatus,
): UseEmployeeDimensionalFilterResult {
  const { showApiError } = useUI();
  const { t: translate } = useTranslation();
  const { createSignal } = useAbortController();

  // Expand/collapse state
  const [isExpanded, setIsExpanded] = useState(false);
  // Ref (not state) so setting it doesn't schedule a redundant re-render.
  // The re-render triggered by setIsExpanded is enough to re-evaluate skipFetch.
  const hasEverExpandedRef = useRef(false);

  const toggleExpanded = useCallback(() => {
    hasEverExpandedRef.current = true;
    setIsExpanded((previous) => !previous);
  }, []);

  // Dimensional filter state
  const [dimensionalFilters, setDimensionalFilters] =
    useState<DimensionalFilterState>(() => buildInitialFilterState(initialStatus));

  // Employee selector: selectedIds and available list from backend
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<number[]>([]);
  // Mirror of selectedEmployeeIds so fetchEmployees can read the current selection
  // without listing it as a dependency (which would recreate the callback on every
  // selection change and retrigger the reload effect).
  const selectedEmployeeIdsRef = useRef<number[]>([]);
  useEffect(() => {
    selectedEmployeeIdsRef.current = selectedEmployeeIds;
  }, [selectedEmployeeIds]);
  const [availableEmployees, setAvailableEmployees] =
    useState<FilteredEmployee[]>([]);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
  const [isLoadingMoreEmployees, setIsLoadingMoreEmployees] = useState(false);
  const [employeePage, setEmployeePage] = useState(1);
  const [employeeHasMore, setEmployeeHasMore] = useState(false);
  const [employeeSearchText, setEmployeeSearchText] = useState<string | null>(null);
  // Cache of selected employee details so they remain visible in options
  // even when not present in the current search/filter results.
  const [selectedEmployeeCache, setSelectedEmployeeCache] =
    useState<Map<number, FilteredEmployee>>(new Map());

  // Debounce dimensional filters so employee reload doesn't fire on every keystroke
  const debouncedFilters = useDebounce(dimensionalFilters, DEBOUNCE_DELAY);
  const debouncedSearchText = useDebounce(employeeSearchText, DEBOUNCE_DELAY);

  // --- Conditional catalog loading via useCatalogLoader ---

  const companyCatalog = useCatalogLoader(
    () => holdingService.list(),
    (company) => ({ value: String(company.id), label: company.name }),
    [],
    !config.company || !hasEverExpandedRef.current,
  );

  const areaCatalog = useCatalogLoader(
    () => areasCatalogService.getAreas(),
    (area) => ({ value: String(area.id), label: area.name }),
    [],
    !config.area || !hasEverExpandedRef.current,
  );

  // Positions depend on selected areas. Backend accepts a comma-separated list
  // (e.g. "1,22,33"); when no area is selected, the catalog is not loaded.
  const selectedAreaIdsCsv = dimensionalFilters.areaIds.length > 0
    ? dimensionalFilters.areaIds.join(',')
    : null;

  const positionCatalog = useCatalogLoader(
    () => positionsCatalogService.getPositions(selectedAreaIdsCsv ?? undefined),
    (position) => ({ value: String(position.id), label: position.name }),
    [selectedAreaIdsCsv],
    !config.position || selectedAreaIdsCsv === null || !hasEverExpandedRef.current,
  );

  const costCenterCatalog = useCatalogLoader(
    () => costCentersCatalogService.getCostCenters(),
    (costCenter) => ({ value: String(costCenter.id), label: costCenter.name }),
    [],
    !config.costCenter || !hasEverExpandedRef.current,
  );

  const locationCatalog = useCatalogLoader(
    (signal) => locationsCatalogService.getLocations(signal),
    (location) => ({ value: String(location.id), label: location.name }),
    [],
    !config.location || !hasEverExpandedRef.current,
  );

  const contractModalityCatalog = useCatalogLoader(
    () => contractCatalogService.getModalities(),
    (modality) => ({ value: String(modality.id), label: modality.name }),
    [],
    !config.contractModality || !hasEverExpandedRef.current,
  );

  const securityGroupCatalog = useSecurityGroupsByPermission(
    !config.securityGroup || !hasEverExpandedRef.current,
  );

  const prevFiltersRef = useRef<DimensionalFilterState | null>(null);

  // --- Employee reload logic ---
  // page === 1 → initial/filtered load (replaces list); page > 1 → infinite scroll (appends).
  const fetchEmployees = useCallback(
    async (
      filters: DimensionalFilterState,
      searchText: string | null,
      shouldPrune: boolean,
      page: number,
    ) => {
      if (!config.employee) return;

      const isFirstPage = page === 1;
      const signal = createSignal();
      if (isFirstPage) {
        setIsLoadingEmployees(true);
      } else {
        setIsLoadingMoreEmployees(true);
      }

      const params: GetEmployeesFilteredParams = {
        companyIds: filters.companyIds,
        areaIds: filters.areaIds,
        positionIds: filters.positionIds,
        costCenterIds: filters.costCenterIds,
        locationIds: filters.locationIds,
        contractModalityIds: filters.contractModalityIds,
        groupIds: filters.groupIds,
        status: filters.status || null,
        searchText: searchText || null,
        page,
        pageSize: EMPLOYEE_FILTER_PAGE_SIZE,
        // Only when pruning (dimensional filter change on page 1) we ask the backend to
        // validate the current selection against the universe so selections living
        // outside page 1 are preserved. validatedSelection drives the prune below.
        ...(shouldPrune && selectedEmployeeIdsRef.current.length > 0
          ? { employeeIds: selectedEmployeeIdsRef.current }
          : {}),
      };

      try {
        const response = await employeeFilteredService.getFiltered(params, signal);

        if (response.error?.code === 'ABORTED') return;

        if (response.success && response.data) {
          const newEmployees = response.data.items;
          setEmployeeHasMore(response.data.pagination.hasNextPage);
          setEmployeePage(response.data.pagination.currentPage);

          if (isFirstPage) {
            setAvailableEmployees(newEmployees);

            if (shouldPrune) {
              // Prune against the backend-validated selection (evaluated against the whole
              // universe, not just page 1), so selections on later pages are preserved
              // when they still satisfy the dimensional filters.
              const validIds = new Set(response.data.validatedSelection);
              setSelectedEmployeeIds((previous) => previous.filter((id) => validIds.has(id)));
            }
          } else {
            // Append next page, deduping by employeeId to guard against overlap
            setAvailableEmployees((previous) => {
              const existingIds = new Set(previous.map((employee) => employee.employeeId));
              const appended = newEmployees.filter(
                (employee) => !existingIds.has(employee.employeeId),
              );
              return [...previous, ...appended];
            });
          }
        } else {
          // Reset pagination so stale values don't keep enabling loadMore retries
          setEmployeeHasMore(false);
          showApiError(response, translate('common:errors.loadingData'));
        }
      } finally {
        // Always clear the loading flag, even on aborted requests
        if (isFirstPage) {
          setIsLoadingEmployees(false);
        } else {
          setIsLoadingMoreEmployees(false);
        }
      }
    },
    [config.employee, createSignal, showApiError, translate],
  );

  const loadMoreEmployees = useCallback(async () => {
    if (isLoadingEmployees || isLoadingMoreEmployees || !employeeHasMore) return;
    await fetchEmployees(debouncedFilters, debouncedSearchText, false, employeePage + 1);
  }, [
    isLoadingEmployees,
    isLoadingMoreEmployees,
    employeeHasMore,
    employeePage,
    debouncedFilters,
    debouncedSearchText,
    fetchEmployees,
  ]);

  // Reload employees when debounced filters or search change.
  // Prune selected IDs only when dimensional filters changed — search text changes
  // (including clearing on selector close) must never invalidate prior selections.
  useEffect(() => {
    const previousFilters = prevFiltersRef.current;
    prevFiltersRef.current = debouncedFilters;
    if (!isExpanded) return;
    const shouldPrune = previousFilters !== null && previousFilters !== debouncedFilters;
    // Filter/search changes always restart at page 1 (replaces the list).
    void fetchEmployees(debouncedFilters, debouncedSearchText, shouldPrune, 1);
  }, [isExpanded, debouncedFilters, debouncedSearchText, fetchEmployees]);

  // Prune cache when selectedEmployeeIds is reduced externally (e.g. dimensional filter prune)
  useEffect(() => {
    setSelectedEmployeeCache((previous) => {
      const selectedSet = new Set(selectedEmployeeIds);
      if ([...previous.keys()].every((id) => selectedSet.has(id))) return previous;
      const next = new Map(previous);
      for (const id of previous.keys()) {
        if (!selectedSet.has(id)) next.delete(id);
      }
      return next;
    });
  }, [selectedEmployeeIds]);

  const employeeOptions = useMemo(() => {
    const merged = new Map<number, { value: string; label: string }>();
    // Include selected employees from cache (may not be in current visible list)
    for (const employee of selectedEmployeeCache.values()) {
      merged.set(employee.employeeId, {
        value: String(employee.employeeId),
        label: employee.displayName,
      });
    }
    // Include current visible employees (overwrites cache entry if both present)
    for (const employee of availableEmployees) {
      merged.set(employee.employeeId, {
        value: String(employee.employeeId),
        label: employee.displayName,
      });
    }
    return [...merged.values()].sort((a, b) => a.label.localeCompare(b.label, 'es'));
  }, [availableEmployees, selectedEmployeeCache]);

  // --- Handlers ---
  const onCompanyChange = useCallback((ids: string | string[]) => {
    setDimensionalFilters((previous) => ({ ...previous, companyIds: toStringArray(ids) }));
  }, []);

  const onAreaChange = useCallback((ids: string | string[]) => {
    const newAreaIds = toStringArray(ids);
    setDimensionalFilters((previous) => ({
      ...previous,
      areaIds: newAreaIds,
      positionIds: [],
    }));
  }, []);

  const onPositionChange = useCallback((ids: string | string[]) => {
    setDimensionalFilters((previous) => ({ ...previous, positionIds: toStringArray(ids) }));
  }, []);

  const onCostCenterChange = useCallback((ids: string | string[]) => {
    setDimensionalFilters((previous) => ({ ...previous, costCenterIds: toStringArray(ids) }));
  }, []);

  const onLocationChange = useCallback((ids: string | string[]) => {
    setDimensionalFilters((previous) => ({ ...previous, locationIds: toStringArray(ids) }));
  }, []);

  const onContractModalityChange = useCallback((ids: string | string[]) => {
    setDimensionalFilters((previous) => ({ ...previous, contractModalityIds: toStringArray(ids) }));
  }, []);

  const onSecurityGroupChange = useCallback((ids: string | string[]) => {
    setDimensionalFilters((previous) => ({ ...previous, groupIds: toStringArray(ids) }));
  }, []);

  const onEmployeeStatusChange = useCallback((status: string | string[]) => {
    const raw = Array.isArray(status) ? status[0] ?? 'vigente' : status;
    setDimensionalFilters((previous) => ({ ...previous, status: raw as EmployeeFilteredStatus }));
  }, []);

  const onEmployeeSelectionChange = useCallback((ids: string | string[]) => {
    const selected = toStringArray(ids).map(Number).filter((n) => !isNaN(n));
    setSelectedEmployeeIds(selected);
    setSelectedEmployeeCache((previous) => {
      const selectedSet = new Set(selected);
      const next = new Map(previous);
      // Add newly selected employees visible in the current list
      for (const employee of availableEmployees) {
        if (selectedSet.has(employee.employeeId)) {
          next.set(employee.employeeId, employee);
        }
      }
      // Remove deselected employees
      for (const id of previous.keys()) {
        if (!selectedSet.has(id)) next.delete(id);
      }
      return next;
    });
  }, [availableEmployees]);

  const onEmployeeSearchChange = useCallback((search: string) => {
    setEmployeeSearchText(search || null);
  }, []);

  return {
    dimensionalFilters,
    selectedEmployeeIds,
    availableEmployees,
    isLoadingEmployees,
    isLoadingMoreEmployees,
    employeeHasMore,
    loadMoreEmployees,
    isLoadingPositions: !positionCatalog.isLoaded,
    isExpanded,
    toggleExpanded,
    catalogOptions: {
      companyOptions: companyCatalog.options,
      areaOptions: areaCatalog.options,
      positionOptions: positionCatalog.options,
      costCenterOptions: costCenterCatalog.options,
      locationOptions: locationCatalog.options,
      contractModalityOptions: contractModalityCatalog.options,
      securityGroupOptions: config.securityGroup ? securityGroupCatalog.options : [],
      employeeOptions,
    },
    handlers: {
      onCompanyChange,
      onAreaChange,
      onPositionChange,
      onCostCenterChange,
      onLocationChange,
      onContractModalityChange,
      onSecurityGroupChange,
      onEmployeeStatusChange,
      onEmployeeSelectionChange,
      onEmployeeSearchChange,
    },
  };
}
